import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, signal, computed, WritableSignal, inject, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, tap, zip } from 'rxjs';

import {
  faCheckCircle,
  faCircleExclamation,
  faPrint,
  faSpinner,
  faQuestionCircle,
  faUsersBetweenLines,
  faBan,
  faShare,
  faLock,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';

import { ChartConfiguration, ChartData, ChartType } from 'chart.js';

import { BaseChartDirective } from 'ng2-charts';

import introJs from 'intro.js';
import { manageSubIntroSteps } from './manage.intro-steps';

import { Student } from '../../../core/models/student.model';
import { SchoolClass } from '../../../core/models/school-class.model';
import { SubscriptionTypes } from '../../../core/models/subscription-types.model';
import { StudentsService } from '../../../core/services/students.service';
import { UtilityService } from '../../../core/services/utility.service';
import { EnrollmentService } from '../../../core/services/enrollment.service';
import { ManagementUser } from '../../../core/models/management-user.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { ReportsService } from '../../../core/services/reports.service';
import { StudentsFilterComponent, StudentsFilterOptions } from '../../../shared/components/students-filter/students-filter.component';
import { PrintService } from 'src/app/core/services/print.service';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { ScoreCutoffs } from '../../../core/models/score-cutoffs.model';
import { AssessmentTypeNames } from '../../../core/models/assessment-types.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SchoolTeacherFilterComponent } from 'src/app/shared/components/school-teacher-filter/school-teacher-filter.component';
import { FormsModule } from '@angular/forms';
import { NgChartsModule } from 'ng2-charts';
import { PercentRoundPipe } from 'src/app/core/pipes/display-percent-rounded-pipe';
import { screenerIntroSteps } from 'src/app/resources/screener.intro-steps';
import { SortColumn, sortUpdate } from 'src/app/core/utils/table';
import { DisplayGradePipe } from 'src/app/core/pipes/display-grade.pipe';
import { StudentDisplay, SubscriptionType } from '../manage-subscription';
import { HIDE_DIAGNOSTIC_SUBSCRIPTIONS } from 'src/app/core/constants/subscription';
import { AssignDiagnosticService } from 'src/app/core/services/assign-diagnostic.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AllAssessmentsCompletedPipe } from '../all-assessments-completed.pipe';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ManageTeacherSubscriptionsComponent } from '../teacher/manage-teacher-subscriptions.component';
import { RemainingSubscriptionsComponent } from '../remaining/remaining-subscriptions.component';
import { ScreenerResultsComponent } from 'src/app/shared/components/screener-results/screener-results.component';
import { UserRoleGroups, UserRoles } from 'src/app/core/models/user.model';
import { SchoolTeacherFilterOptions } from 'src/app/core/models/filters';
import { MainContentComponent } from 'src/app/shared/components/main-content/main-content.component';

@Component({
  selector: 'wf-manage-student-subscriptions',
  templateUrl: './manage-student-subscriptions.component.html',
  styleUrls: ['./manage-student-subscriptions.component.css'],
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NgChartsModule,
    FormsModule,
    SchoolTeacherFilterComponent,
    StudentsFilterComponent,
    FontAwesomeModule,
    PercentRoundPipe,
    DisplayGradePipe,
    DatePipe,
    AllAssessmentsCompletedPipe,
    RemainingSubscriptionsComponent,
    ScreenerResultsComponent,
    MainContentComponent,
  ]
})
export class ManageStudentSubscriptionsComponent implements OnInit, AfterViewInit {
  readonly assignDiagnosticService = inject(AssignDiagnosticService);
  readonly sessionStorageService = inject(SessionStorageService);
  readonly loadingService = inject(LoadingService);
  readonly reportService = inject(ReportsService);

  readonly currentUser = inject(SessionStorageService).userData;

  readonly UserRoles = UserRoles;

  readonly HIDE_DIAGNOSTIC_SUBSCRIPTIONS = HIDE_DIAGNOSTIC_SUBSCRIPTIONS;
  readonly COLUMNS = {
    DIAGNOSTIC_SUB: 'Diagnostic',
    FULL_SUB: 'Diagnostic',
    STUDENT_NAME: 'Student Name',
    GRADE: 'Grade',
    RESULTS: 'Results',
    DATE_STARTED: 'Date Started',
    DATE_COMPLETED: 'Date Completed',
    PERCENT_COMPLETE: 'Percent Complete',
  } as const;

  readonly DATE_FORMAT = 'MM/dd/YYYY';

  readonly loading = signal(false);

  readonly schoolTeacherFilters = signal<SchoolTeacherFilterOptions | undefined>(undefined);
  readonly filters = signal<StudentsFilterOptions>({
    grade: '',
    class: '',
    extraSupport: ''
  });
  readonly sortColumn = signal<SortColumn>({
    column: this.COLUMNS.STUDENT_NAME,
    reverse: false,
  });

  // let schoolID = this.reportService.getSelectedSchoolForReports().schoolID ;
  // let teacherID = this.reportService.getSelectedTeacherForReports().userID ;

  readonly schoolTeacherIDs = computed(() => {
    const user = this.sessionStorageService.userData();
    if (!user) {
      return undefined;
    }

    if (
      user.userRole !== UserRoles.SCHOOL_ADMIN
      && user.effectiveUserRole !== UserRoles.SCHOOL_ADMIN
      && user.userRole !== UserRoles.CUSTOMER_SERVICE
    ) {
      return {
        // TODO: is this safe in a computed? The old resolver service seems to expect it to be populated at route load
        schoolID: this.reportService.getSelectedSchoolForReports().schoolID,
        teacherID: this.reportService.getSelectedTeacherForReports().userID,
      };
    }

    // for School Admins and Customer Service who can look at students for teachers that aren't themselves
    const schoolTeacherFilters = this.schoolTeacherFilters();
    if (!schoolTeacherFilters?.teacher?.userID) {
      return undefined;
    }

    return {
      schoolID: schoolTeacherFilters.teacher.schoolID,
      teacherID: schoolTeacherFilters.teacher.userID,
    }
  });

  readonly screenerData = toSignal(
    toObservable(this.schoolTeacherIDs).pipe(
      switchMap((schoolTeacherIDs => {
        if (!schoolTeacherIDs) {
          return of({
            schoolID: 0,
            teacherID: 0,
            grades: [],
            classes: [],
            students: [],
            diagnosticLicenses: 0,
            interventionLicenses: 0,
          });
        }
        return this.reportService
          .getScreenerReportData(schoolTeacherIDs.schoolID, schoolTeacherIDs.teacherID, false)
          .pipe(map(d => ({ ...schoolTeacherIDs, ...d })));
      })
    ))
  );

  // readonly students = signal<Student[]>([]);
  readonly studentsFiltered = computed(() => this.filterStudents());
  readonly studentsFilteredDisplay = computed(() => this.studentsFiltered().map(s => this.studentDisplay(s)));
  readonly studentsFilteredDisplaySorted = computed(() => this.studentsFilteredDisplay().sort((s1, s2) => this.studentDisplayComparator(s1, s2)));

  // If admin user, count subs with NULL/NULL for TeacherID/StudentID, if teacher user, it count of TeacherID/NULL for TeacherID/StudentID

  // TODO: use an httpResource when they are stable
  readonly subscriptionsRefresh = new BehaviorSubject<void>(undefined);
  readonly subscriptions = toSignal(
    combineLatest([
      toObservable(this.currentUser),
      toObservable(this.screenerData),
      this.subscriptionsRefresh
    ]).pipe(
      tap(() => this.loadingService.start()),
      switchMap(([user, screenerData]) => {
        if (!user || !screenerData || !screenerData.students || !screenerData.students.length) {
          return of(undefined);
        }
        const studentIDs = screenerData.students.map((s: any) => s.userID);
        const userID = (
          user.userRole === UserRoles.SCHOOL_ADMIN
          || user.effectiveUserRole === UserRoles.SCHOOL_ADMIN
          || user.userRole === UserRoles.CUSTOMER_SERVICE
        ) ? 0 : user.userID;

        return zip([
          this.assignDiagnosticService.getNumSubscriptionsRemaining(screenerData.schoolID, userID),
          this.assignDiagnosticService.getSubscriptionsForStudents(studentIDs)
        ]).pipe( map(([remaining, students]) => ({ remaining, students }) ));
      }),
      tap(() => this.loadingService.finish()),
    )
  );

  // TODO: Clear this every time filters change?
  // TODO: Make this a linkedSignal and delete the effect
  readonly selectedStudentUserIDs = signal<
    { [sub in "diagnostic" | "system"]: { [userID: number]: boolean } }
    >({
      diagnostic: {},
      system: {}
    });
  readonly anyStudentHasDiagnosticSubs = computed(() => {
    return Object.values(this.selectedStudentUserIDs().diagnostic).some(d => d);
  });

  protected readonly populateSelectedStudentUserIDsEffect = effect(() => {
    const subscriptions = this.subscriptions();
    if (!subscriptions) {
      return;
    }
    const studentSubscriptions = subscriptions.students;

    const studentsWithNonConsumedDiagnosticSub: { [userID: number]: boolean } = {};
    Object.keys(studentSubscriptions).filter(id => {
      const sub = studentSubscriptions[id][1]
      return sub && !sub.consumed;
    }).forEach(id => { studentsWithNonConsumedDiagnosticSub[parseInt(id)] = true; });

    const studentsWithNonConsumedSystemSub: { [userID: number]: boolean } = {};
    Object.keys(studentSubscriptions).filter(id => {
      const sub = studentSubscriptions[id][2]
      return sub && !sub.consumed;
    }).forEach(id => { studentsWithNonConsumedSystemSub[parseInt(id)] = true; });

    this.selectedStudentUserIDs.set({
      diagnostic: studentsWithNonConsumedDiagnosticSub,
      system: studentsWithNonConsumedSystemSub
    })
  })

  readonly studentsNotStarted = computed(() =>
    this.studentsFiltered()
      .filter(s => s.firstAssessmentScore && s.firstAssessmentScore.isActive && !s.firstAssessmentScore.dateStarted).length
  );
  readonly studentsSkipped = computed(() =>
    this.studentsFiltered()
      .filter(s => s.firstAssessmentScore && !s.firstAssessmentScore.isActive && !s.firstAssessmentScore.dateCompleted).length
  );
  readonly studentsStarted = computed(() =>
    this.studentsFiltered().length - this.studentsNotStarted() - this.studentsSkipped()
  );


  readonly notStartedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsNotStarted() / this.filterStudents().length) * 100) : 0 ) ;
  readonly startedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsStarted() / this.filterStudents().length) * 100) : 0 );
  readonly skippedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsSkipped() / this.filterStudents().length) * 100) : 0 );

  readonly tierLabels = computed(() => {
    const filterOpts = this.filters();
    // Grade value that comes from the filter is our true, displayed grade (not our internal grade, so 4 is 4th grade)
    let gradeNum = parseInt(filterOpts.grade) ;
    let selectedGradeGroup = (filterOpts.grade === 'four_below_all' || (!isNaN(gradeNum) && gradeNum <= 4)) ? 'elementary' : 'secondary' ;
    if (filterOpts.grade === 'all')
    {
      return  {
        'proficient' : '',
        'some-risk' : '',
        'high-risk' : '',
      } ;
    }
    return  {
      'proficient' : ScoreCutoffs[selectedGradeGroup]['labels']['topTier']['range'],
      'some-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['middleTier']['range'],
      'high-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['bottomTier']['range'],
    } ;
  })

  toggleSub(level: 'diagnostic' | 'system', student: StudentDisplay) {
    const studentID = student.userID;
    const hasSub = this.selectedStudentUserIDs()[level][studentID] ?? false;
    const subType = level === 'diagnostic' ? SubscriptionType.DIAGNOSTIC : SubscriptionType.INTERVENTION;
    const user = this.sessionStorageService.userData();

    if (!user) {
      console.error('User data not found');
      return;
    }

    const sub = this.subscriptions()?.students[studentID]?.[subType];
    if (hasSub && !sub) {
      console.error('Existing sub not found');
      return;
    }

    if (sub && sub.consumed) {
      console.error('Trying to delete a consumed subscription')
    }

    let request: Observable<any>;
    if (level === 'diagnostic') {
      request = hasSub
        ? this.assignDiagnosticService.removeDiagnosticFromStudent(studentID, sub.subscriptionID)
        : this.assignDiagnosticService.assignDiagnosticToStudent(studentID, student.teacherID);
    } else {
      request = hasSub
        ? this.assignDiagnosticService.removeFullProductFromStudent(studentID, sub.subscriptionID)
        : this.assignDiagnosticService.assignFullProductToStudent(studentID, student.teacherID);
    }

    // this.loadingService.start();
    request.subscribe({
      next: () => {
        this.subscriptionsRefresh.next();
        // this.loadingService.finish();
      },
      error: () => {
        // this.loadingService.finish();
      }
    })
  }

  showSchoolFilter: boolean = false ;
  showConfirmDialog: boolean = false ;
  confirmSkipScreenerTo: string = '' ;
  schoolFilterError: string = '' ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  // selectedStudents: Set<number> = new Set<number>();
  studentsAreSelected: boolean = false ;
  // students: Student[] = [] ;
  filteredStudents: Student[] = [] ;
  filteredStudentsWithCoTeachers: Student[] = [] ;
  readonly grades = signal<string[]>([]);
  readonly classes = signal<SchoolClass[]>([]);
  highRiskCnt: number = 0 ;
  someRiskCnt: number = 0 ;
  proficientCnt: number = 0 ;
  chartType: ChartType = 'pie' ;
  chartData: ChartData<'pie', number[], string | string[]> = {
    labels: [ 'Proficient', 'Some Risk', 'High Risk' ],
    datasets: [{
      data: [ this.proficientCnt, this.someRiskCnt, this.highRiskCnt ],
      backgroundColor: [
        '#58B957FF',
        '#F2AE43',
        '#DB524B'
      ],
      hoverBackgroundColor: [
        '#58B957',
        '#F2AE43',
        '#DB524B'
      ],
    }]
  } ;
  hasChartData: boolean = false ;
  chartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (toolTip) => {
            //let totalStudents: number = toolTip.dataset['data'].reduce((prev: number, curr: number) => { return prev! + curr! }, 0) ;
            let totalStudents = 0 ;
            toolTip.dataset['data'].forEach((val: any) => totalStudents += (val as number)) ;

            return (totalStudents === 0) ? '' : `${Math.round((toolTip.parsed / totalStudents) * 100)}%` ;
          }
        }
      }
    },
  } ;
  printIcon = faPrint ;
  loadingIcon = faSpinner ;
  lockIcon = faLock;
  readonly enrollIcon = faUserPlus;
  diagnosticTitle = SubscriptionTypes.DiagnosticProduct ;
  systemTitle = SubscriptionTypes.FullProduct ;
  readonly unassignedDiagnosticLicenses = signal(0);
  readonly unassignedSystemLicenses = signal(0);

  tableData: any[] = [] ;
  sortReverse: boolean = false ;
  message: string = '' ;
  messageTitle: string = '' ;
  messageStatus: string = '' ;
  successIcon = faCheckCircle ;
  errorIcon = faCircleExclamation ;

  studentScoreRange: string = '' ;
  processingDiag: boolean = false ;
  processingFull: boolean = false ;
  helpIcon = faQuestionCircle;
  coTeacherIcon = faUsersBetweenLines;
  introTour: any;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective ;
  @ViewChild(StudentsFilterComponent) studentFilter?: StudentsFilterComponent ;
  @ViewChild('screenerSummaryCanvas') screenerSummaryCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('screenerSummaryImg') screenerSummaryImg!: ElementRef<HTMLImageElement> ;
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;

  constructor(
    private route: ActivatedRoute,
    private enrollmentService: EnrollmentService,
    private studentService: StudentsService,
    private utilityService: UtilityService,
    private printService: PrintService
  ) { }

  ngOnInit(): void {
    const currentUser = this.currentUser();
    // Filter data
    const filterData = this.route.snapshot.data['filterData'];
    this.districts = filterData.districts;
    this.schools = filterData.schools;
    this.teachers = currentUser?.userRole === UserRoles.CUSTOMER_SERVICE ? [] : filterData.teachers.filter((t: any) => t.userID) // filter out the 'All' option ;

    if (this.route.snapshot.data['resolveData'].erred)
    {
      this.schoolFilterError = this.route.snapshot.data['resolveData'].message ;
    }
    else
    {

      // this.students.set(this.route.snapshot.data['resolveData'].students);
      this.grades.set(this.route.snapshot.data['resolveData'].grades);
      this.classes.set(this.route.snapshot.data['resolveData'].classes);

      this.unassignedDiagnosticLicenses.set(this.route.snapshot.data['resolveData'].diagnosticLicenses);
      this.unassignedSystemLicenses.set(this.route.snapshot.data['resolveData'].interventionLicenses);
    }

    if (currentUser?.isFILUser() || currentUser?.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    // // if (canViewSchoolFilter(this.currentUser))
    // if (true)
    // {
    //   // The FIL user and District and School user roles can select all teachers
    //   this.teachers.unshift(ManagementUser.getGenericUser()) ;
    // }

    this.filterReportData() ;
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  ngOnDestroy(): void {
    this.loadingService.finish();
  }

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }

      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: manageSubIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showSkip: false,
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(manageSubIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps });
    this.introTour.start();
  }

  filterStudents() {
    // TODO: Improve error handling when httpResource is available
    const students = (this.screenerData() as any)?.students;
    if (!students) {
      return [];
    }
    return this.studentService.filterStudents(students, this.filters()) ;
  }

  filterSchoolTeacher() {
    let schoolID = this.reportService.getSelectedSchoolForReports().schoolID ;
    let teacherID = this.reportService.getSelectedTeacherForReports().userID ;


    this.reportService.getScreenerReportData(schoolID, teacherID, false).subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message;
          // this.students.set([]);
          this.grades.set([]);
          this.classes.set([]);
          this.filteredStudents = [];
          this.filterReportData();
        }
        else
        {
          this.schoolFilterError = '' ;
          // this.students.set(data.students);
          this.grades.set(data.grades);
          this.classes.set(data.classes);
          this.unassignedDiagnosticLicenses.set(data.diagnosticLicenses);
          this.unassignedSystemLicenses.set(data.interventionLicenses);
          // this.filteredStudents = this.students ;

          this.filterReportData() ;
        }
      }
    }) ;
  }

  // Asserts if a student can be selected by the (teacher) user.
  // Students they've been given report access to cannot be selected, only students
  // that are explicitly assigned to them.
  isPrimaryTeacherForStudent(student: Student) {
    const selectedTeacherId = this.getTeacherIdForAssignment();
    if (!selectedTeacherId) {
      return false;
    }

    return selectedTeacherId === student.teacherID;
  }

  canSelectStudentsForAssignment() {
    return this.getTeacherIdForAssignment() > 0;
  }

  private getTeacherIdForAssignment(): number {
    const user = this.currentUser();
    if (user?.isTeacherUser()) {
      return user.userID;
    }

    if (user?.isSchoolAdmin() || user?.isCustomerServiceUser()) {
      return this.reportService.getSelectedTeacherForReports().userID;
    }

    return 0;
  }

  toggleSharedStudents() {
    this.filterReportData() ;
  }


  printSummary() {
    let screenerSummaryCanvas = this.screenerSummaryCanvas.nativeElement;
    let screenerSummaryImg = this.screenerSummaryImg.nativeElement;
    this.reportService.convertCanvasToImage(screenerSummaryCanvas, screenerSummaryImg);
    let school = this.reportService.getSelectedSchoolForReports().name;
    let teacher = this.reportService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }


  private studentDisplay(student: Student): StudentDisplay {
    // This is ported over from the legacy app, but this logic makes it appear that
    // we are iterating over all assessments, so if there was more than one sent back
    // we could be counting extra (more than 1 assessment per student) -- however, it
    // looks like when the call to `/screenerreports` is made, the query returns just
    // the Screener data, so a single assessment
    let studentGroup = (student.grade <= 5) ? 'elementary' : 'secondary' ;
    student.assessmentScores?.forEach((assessment) => {
      if (assessment && assessment.dateCompleted)
      {
        if (assessment.systemScore < ScoreCutoffs[studentGroup]['automaticity']['some-risk']) this.highRiskCnt++ ;
        else if (assessment.systemScore < ScoreCutoffs[studentGroup]['automaticity']['proficient']) this.someRiskCnt++ ;
        else this.proficientCnt++ ;
      }
    }) ;

    // Set our screener results class
    // FUTURE: Could this be moved to the assessment score model as a method?
    let screenerClass = '' ;
    if (student.firstAssessmentScore && student.firstAssessmentScore.dateCompleted)
    {
      if (student.firstAssessmentScore.systemScore < ScoreCutoffs[studentGroup]['automaticity']['some-risk'])
      {
        screenerClass = 'score-red' ;
      }
      else if (student.firstAssessmentScore.systemScore < ScoreCutoffs[studentGroup]['automaticity']['proficient'])
      {
        screenerClass = 'score-yellow' ;
      }
      else
      {
        screenerClass = 'score-green' ;
      }
    }

    const scoreRange =  (!student.firstAssessmentScore || student.firstAssessmentScore?.screenerResultsLow < 0 || student.firstAssessmentScore!.screenerResultsHigh < 0)
      ? '0'
      : `${student.firstAssessmentScore!.screenerResultsLow} - ${student.firstAssessmentScore!.screenerResultsHigh}`;

    // Push to our tableData array, which contains simplified (and thus sortable) data to display
    let percentComplete = 0 ;
    let skipped = false ;
    if (student.firstAssessmentScore && student.firstAssessmentScore.assessmentType === AssessmentTypeNames.SCREENER)
    {
      skipped = !student.firstAssessmentScore.dateCompleted && !student.firstAssessmentScore.isActive ;
      if (!student.firstAssessmentScore.numDiagnosticTasks)
      {
        percentComplete = 0 ;
      }
      else
      {
        percentComplete = (student.firstAssessmentScore.numDiagnosticTasksCompleted / student.firstAssessmentScore.numDiagnosticTasks) * 100 ;
      }
    }

    return {
      ...student,
      scoreRange,
      percentComplete,
      skipped,
      screenerClass
    };
  }

  private studentDisplayComparator(s1: StudentDisplay, s2: StudentDisplay) {
    const { column, reverse } = this.sortColumn();

    const nameComp = s1.firstName.localeCompare(s2.firstName) || s1.lastName.localeCompare(s2.lastName);

    let comp = nameComp;
    if (column === this.COLUMNS.GRADE) {
      comp = s1.grade - s2.grade;
    } else if (column === this.COLUMNS.RESULTS) {
      comp = (s1.firstAssessmentScore?.screenerResultsLow ?? 0) - (s2.firstAssessmentScore?.screenerResultsLow ?? 0);
    } else if (column === this.COLUMNS.DATE_STARTED) {
      comp = (s1.firstAssessmentScore?.dateStarted ?? 0) - (s2.firstAssessmentScore?.dateStarted ?? 0);
    } else if (column === this.COLUMNS.DATE_COMPLETED) {
      comp = (s1.firstAssessmentScore?.dateCompleted ?? 0) - (s2.firstAssessmentScore?.dateCompleted ?? 0);
    } else if (column === this.COLUMNS.PERCENT_COMPLETE) {
      comp = s1.percentComplete - s2.percentComplete;
    }

    if (comp === 0 && column !== this.COLUMNS.STUDENT_NAME) {
      // On tie of non-name columns, fall back to ascending name
      // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
      return nameComp;
    }

    return reverse ? (comp * -1) : comp
  }

  private filterReportData() {
    this.highRiskCnt = 0 ;
    this.someRiskCnt = 0 ;
    this.proficientCnt = 0 ;
    this.tableData = [] ;

    this.chartData.datasets[0].data = [ this.proficientCnt, this.someRiskCnt, this.highRiskCnt ] ;

    this.hasChartData = this.chartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0) > 0 ;

    this.chart?.update() ;
  }

  protected readonly cancelIcon = faBan;
  protected readonly skipIcon = faShare;
}
